import { Button } from "@shared-ui/button";
import { Separator } from "@shared-ui/separator";
import {
	ClipboardList,
	FolderTree,
	KeyRound,
	LayoutDashboard,
	Library,
	LogOut,
	Percent,
	Receipt,
	ScrollText,
	ShieldCheck,
	Store,
	Tags,
	Upload,
	Users,
} from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { type AdminNavSection, canSeeNav } from "@/lib/permissions";
import { cn } from "@/lib/utils";

// Derived, not restated. See AdminNavSection in lib/permissions.
type NavSection = AdminNavSection;

const navigation: {
	label: string;
	to: string;
	icon: typeof ShieldCheck;
	section: NavSection;
	/**
	 * Match this path EXACTLY rather than as a prefix.
	 *
	 * `NavLink` without it matches descendants, so `/catalog` renders active on
	 * every `/catalog/*` page -- two entries in the selected style at once, and
	 * no way to tell from the sidebar whether you are on the dashboard. Only the
	 * section root needs it; the leaf entries are not prefixes of each other.
	 */
	end?: boolean;
}[] = [
	{
		label: "Approvals",
		to: "/approvals",
		icon: ShieldCheck,
		section: "approvals",
	},
	{ label: "Payments", to: "/payments", icon: Receipt, section: "payments" },
	{ label: "Users", to: "/users", icon: Users, section: "users" },
	// Points at the dashboard, not the product list. "What needs me today" is the
	// question an operator opens the section with; the list is where they go once
	// they know which products they are looking for.
	{
		label: "Catalogue",
		to: "/catalog",
		icon: LayoutDashboard,
		section: "catalog",
		end: true,
	},
	{
		label: "Products",
		to: "/catalog/products",
		icon: Library,
		section: "catalog",
	},
	// A second catalogue entry rather than a sub-nav: brands are a destination an
	// operator goes to directly (to merge duplicates, to withdraw a range), not
	// somewhere reached by drilling through products. Both gate on the same
	// section, so an operator without catalogue access sees neither.
	{
		label: "Brands",
		to: "/catalog/brands",
		icon: Tags,
		section: "catalog",
	},
	// And the taxonomy, for the same reason brands get their own entry: an
	// operator goes to the tree deliberately -- to add a range's categories
	// before loading it -- rather than arriving from a product.
	{
		label: "Categories",
		to: "/catalog/categories",
		icon: FolderTree,
		section: "catalog",
	},
	// Imports, for the same reason brands and categories get their own entries:
	// loading a price list is a thing an operator sits down to do, not somewhere
	// they arrive from a product. It gates on the catalogue section like the
	// rest; the ROUTE additionally requires `admin:catalog:import`, so an
	// operator without it sees the link and is told what is missing rather than
	// meeting a screen whose every request 403s.
	{
		label: "Imports",
		to: "/catalog/imports",
		icon: Upload,
		section: "catalog",
	},
	// GST rates (#514): its own section so finance_ops, which holds only
	// `admin:catalog:gst`, sees this entry and no other catalogue one.
	{
		label: "GST rates",
		to: "/catalog/gst-rates",
		icon: Percent,
		section: "gst",
	},
	{
		label: "BOQ templates",
		to: "/boq-templates",
		icon: ClipboardList,
		section: "boqTemplates",
	},
	{ label: "Vendors", to: "/vendors", icon: Store, section: "vendors" },
	{ label: "Logs", to: "/logs", icon: ScrollText, section: "logs" },
	{ label: "Roles", to: "/roles", icon: KeyRound, section: "roles" },
];

export function AdminShell() {
	const navigate = useNavigate();
	const { operator, logout } = useAdminAuthStore();

	return (
		<div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#eef2f7_100%)] text-slate-950">
			<div className="mx-auto flex min-h-screen max-w-[1600px] flex-col px-4 py-4 md:px-6">
				<header className="mb-4 flex items-center justify-between rounded-3xl border border-white/70 bg-white/90 px-5 py-4 shadow-sm shadow-slate-200/70 backdrop-blur">
					<div className="flex items-center gap-3">
						<div className="flex size-11 items-center justify-center rounded-2xl bg-slate-950 text-white">
							<LayoutDashboard className="size-4" />
						</div>
						<div>
							<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
								Finishd operations
							</p>
							<h1 className="text-lg font-semibold">Platform admin</h1>
						</div>
					</div>
					<div className="flex items-center gap-4">
						<div className="hidden text-right md:block">
							<p className="text-sm font-medium">{operator?.operator.phone}</p>
							<p className="text-xs text-muted-foreground">
								Permissions:{" "}
								{operator?.authorization?.permissions?.join(", ") ?? "—"}
							</p>
						</div>
						<Button
							variant="outline"
							onClick={async () => {
								await logout();
								navigate("/login", { replace: true });
							}}
						>
							<LogOut data-icon="inline-start" />
							Log out
						</Button>
					</div>
				</header>

				<div className="grid flex-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
					<aside className="rounded-3xl border border-white/70 bg-white/90 p-3 shadow-sm shadow-slate-200/70 backdrop-blur">
						<nav className="flex flex-col gap-2">
							{navigation
								.filter((item) => canSeeNav(operator, item.section))
								.map((item) => {
									const Icon = item.icon;
									return (
										<NavLink
											key={item.to}
											to={item.to}
											end={item.end ?? false}
											className={({ isActive }) =>
												cn(
													"flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition",
													isActive
														? "bg-slate-950 text-white shadow-lg shadow-slate-300/30"
														: "text-slate-600 hover:bg-slate-100",
												)
											}
										>
											<Icon className="size-4" />
											{item.label}
										</NavLink>
									);
								})}
						</nav>
						<Separator className="my-4" />
						<p className="px-4 text-xs leading-5 text-muted-foreground">
							Dense tables, side inspectors, and backend-validated actions. The
							goal is fewer context switches, not a prettier CRM.
						</p>
					</aside>
					<main className="min-w-0 rounded-[28px] border border-white/70 bg-white/85 p-4 shadow-sm shadow-slate-200/70 backdrop-blur md:p-5">
						{/*
						 * Inner boundary so a render crash in one dashboard doesn't take
						 * out the chrome (sidebar/logout). The outer boundary in App.tsx
						 * catches anything that escapes this one.
						 */}
						<ErrorBoundary label="AdminShell">
							<Outlet />
						</ErrorBoundary>
					</main>
				</div>
			</div>
		</div>
	);
}
